import React, { createContext, useContext, useState, useEffect } from 'react';

interface ClientContextProps {
  clientIdContext: number;
  clientType: string;
  clientDescriptionContext: string;
  clientTypeIdContext: number;
  clientTypeDescContext: string;
  clientModuleCon: string[];
  clientModuleOptionCon: string[];
  clientStatusContext: string;
  clientsSelectedIntellicaAdmin: string[];
  clientsNameSelectedIntellicaAdmin: string[];
  updateClientIdContext: (newClientId: number) => void;
  updateClientType: (newClientType: string) => void;
  updateClientTypeId: (newClientTypeId: number) => void;
  updateClientTypeDescription: (newClientTypeDesc: string) => void;
  updateClientDescription: (newClientDesc: string) => void;
  updateClientModule: (newClientModule: string[]) => void;
  updateClientModuleOptions: (newClientModuleOptions: string[]) => void;
  updateClientStatus: (newStatus: string) => void;
  updateClientSelectedIntellicaAdmin: (clientsSelected: string[]) => void
  updateClientSelectedNameIntellicaAdmin: (clientsSelectedName: string[]) => void
}

const ClientContext = createContext<ClientContextProps | undefined>(undefined);

export const useClientContext = () => {
  const context = useContext(ClientContext);
  if (!context) {
    throw new Error('useClientContext must be used within a ClientContextProvider');
  }
  return context;
};

interface ClientContextProviderProps {
  children: React.ReactNode;
}

const ClientContextProvider: React.FC<ClientContextProviderProps> = ({ children }) => {
  const parseJSON = <T,>(value: string | null): T | null => {
    try {
      return value ? JSON.parse(value) : null;
    } catch {
      return null;
    }
  };

  const [clientIdContext, setClientIdContext] = useState<number>(() => {
    const savedValue = localStorage?.getItem('clientIdContext');
    return parseJSON<number>(savedValue) ?? 0;
  });

  const [clientType, setClientType] = useState<string>(() => {
    const savedValue = localStorage?.getItem('clientType');
    return savedValue ?? '';
  });

  const [clientDescriptionContext, setClientDescriptionContext] = useState<string>(() => {
    const savedValue = localStorage?.getItem('clientDescriptionContext');
    return savedValue ?? '';
  });

  const [clientModuleCon, setClientModuleCon] = useState<string[]>(() => {
    const savedValue = localStorage?.getItem('clientModuleCon');
    return parseJSON<string[]>(savedValue) ?? [];
  });

  const [clientModuleOptionCon, setClientModuleOptionCon] = useState<string[]>(() => {
    const savedValue = localStorage?.getItem('clientModuleOptionCon');
    return parseJSON<string[]>(savedValue) ?? [];
  });

  const [clientTypeIdContext, setClientTypeIdContext] = useState<number>(() => {
    const savedValue = localStorage?.getItem('clientTypeIdContext');
    return parseJSON<number>(savedValue) ?? 0;
  });

  const [clientTypeDescContext, setClientTypeDescContext] = useState<string>(() => {
    const savedValue = localStorage?.getItem('clientTypeDescContext');
    return savedValue ?? '';
  });

  const [clientStatusContext, setClientStatusContext] = useState<string>(() => {
    const savedValue = localStorage?.getItem('clientStatusContext');
    return savedValue ?? '';
  });

  const [clientsSelectedIntellicaAdmin, setClientsSelectedIntellicaAdmin] = useState<string[]>([]);
  const [clientsNameSelectedIntellicaAdmin, setClientsNameSelectedIntellicaAdmin] = useState<string[]>([]);

  useEffect(() => {
    localStorage?.setItem('clientIdContext', JSON.stringify(clientIdContext));
  }, [clientIdContext]);

  useEffect(() => {
    localStorage?.setItem('clientType', clientType);
  }, [clientType]);

  useEffect(() => {
    localStorage?.setItem('clientDescriptionContext', clientDescriptionContext);
  }, [clientDescriptionContext]);

  useEffect(() => {
    localStorage?.setItem('clientModuleCon', JSON.stringify(clientModuleCon));
  }, [clientModuleCon]);

  useEffect(() => {
    localStorage?.setItem('clientModuleOptionCon', JSON.stringify(clientModuleOptionCon));
  }, [clientModuleOptionCon]);

  useEffect(() => {
    localStorage?.setItem('clientTypeIdContext', JSON.stringify(clientTypeIdContext));
  }, [clientTypeIdContext]);

  useEffect(() => {
    localStorage?.setItem('clientTypeDescContext', clientTypeDescContext);
  }, [clientTypeDescContext]);

  useEffect(() => {
    localStorage?.setItem('clientStatusContext', clientStatusContext);
  }, [clientStatusContext]);

  const updateClientIdContext = (newClientId: number) => {
    setClientIdContext(newClientId);
  };

  const updateClientType = (newClientType: string) => {
    setClientType(newClientType);
  };

  const updateClientDescription = (newClientDesc: string) => {
    setClientDescriptionContext(newClientDesc);
  };

  const updateClientTypeId = (newClientTypeId: number) => {
    setClientTypeIdContext(newClientTypeId);
  };

  const updateClientTypeDescription = (newClientTypeDesc: string) => {
    setClientTypeDescContext(newClientTypeDesc);
  };

  const updateClientModule = (newClientModule: string[]) => {
    setClientModuleCon(newClientModule);
  };

  const updateClientModuleOptions = (newClientModuleOptions: string[]) => {
    setClientModuleOptionCon(newClientModuleOptions);
  };

  const updateClientStatus = (newStatus: string) => {
    setClientStatusContext(newStatus);
  };

  const updateClientSelectedIntellicaAdmin = (clientsSelected: string[]) => {
    setClientsSelectedIntellicaAdmin(clientsSelected);
  };

  const updateClientSelectedNameIntellicaAdmin = (clientsSelectedName: string[]) => {
    setClientsNameSelectedIntellicaAdmin(clientsSelectedName);
  };

  const contextValue: ClientContextProps = {
    clientIdContext,
    clientType,
    clientDescriptionContext,
    clientTypeIdContext,
    clientTypeDescContext,
    clientModuleCon,
    clientModuleOptionCon,
    clientStatusContext,
    clientsSelectedIntellicaAdmin,
    clientsNameSelectedIntellicaAdmin,
    updateClientIdContext,
    updateClientType,
    updateClientTypeId,
    updateClientTypeDescription,
    updateClientDescription,
    updateClientModule,
    updateClientModuleOptions,
    updateClientStatus,
    updateClientSelectedIntellicaAdmin,
    updateClientSelectedNameIntellicaAdmin,
  };

  return <ClientContext.Provider value={contextValue}>{children}</ClientContext.Provider>;
};

export default ClientContextProvider;

